import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { RestApiBase } from '../shared/configurations/rest-api-base.config';
import { UrlUtils } from '../shared/utils/url.utils';
import { InciteMetricsDTO } from '../shared/external-model/incitemetrics/incite-metrics-dto';
import { SchemaValueDTO } from '../shared/external-model/incitemetrics/schema-value-dto';
import { AssetRevisionChangesDTO } from '../shared/interfaces/asset/revision/asset-revision-changes-dto';
import { AssetRevisionSubmissionDTO } from '../shared/interfaces/asset/revision/asset-revision-submission-dto';
import { AssetRevisionChangeDTO } from '../shared/interfaces/asset/revision/asset-revision-change-dto';
import { EntityService } from '../core/entity.service';
import { Observable, of, shareReplay, switchMap } from 'rxjs';
import { EsploroMetricSourceCode } from '../shared/interfaces/esploro-metric-source';
import { AssetForPortalFullAssetPage } from '../shared/interfaces/research-portal/full-asset-page/asset-for-portal-full-asset-page.interface';
import { AssetFieldTypeEnum } from '../shared/interfaces/asset/revision/asset-field-type';
import { EntityLocalFields } from '../shared/interfaces/entitiy-local-fields.interface';

@Injectable({
    providedIn: 'root',
})
export class AssetPageService extends EntityService {
    instCode: string;
    private _alreadyView = false;
    private _forceView = false;
    pageData$: Observable<AssetForPortalFullAssetPage>;

    constructor() {
        super();
        this.instCode = UrlUtils.getParam('institution');
        this.pageData$ = this.entityId$.pipe(
            switchMap((id) => this.getFullAssetPageByMmsId(id)),
        );
    }

    setForceView(forceView: boolean) {
        this._forceView = forceView;
    }

    getFullAssetPageByMmsId(id) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('language', this.i18nSvc.getLanguage())
            .set('forceView', this._forceView)
            .set('userId', this.jwtSvc.getUserID());
        return this.http
            .get<AssetForPortalFullAssetPage>(
                RestApiBase.GET_ASSET_FUILL_PAGE_BY_MMS_ID + '/' + id,
                { params },
            )
            .pipe(shareReplay());
    }

    getUsageData(mmsId) {
        const params = new HttpParams().set('institution', this.instCode);
        return this.http
            .get(RestApiBase.GET_ASSET_METRICS_USAGE + '?mmsId=' + mmsId, {
                params,
            })
            .pipe(shareReplay());
    }

    getWOSCitationTotal(mmsId) {
        const params = new HttpParams().set('institution', this.instCode);
        return this.http
            .get(RestApiBase.GET_WOS_CITATION_TOTAL + '?mmsId=' + mmsId, {
                params,
            })
            .pipe(shareReplay());
    }

    getCitationTotal(mmsId: string, source: EsploroMetricSourceCode) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('mmsId', mmsId)
            .set('source', source);

        return this.http
            .get<number>(RestApiBase.GET_CITATION_TOTAL, { params })
            .pipe(shareReplay());
    }

    getCitation(mmsIds: string[], style) {
        let params = new HttpParams();
        params = params.set('institution', this.instCode);
        const citationJson = { mmsIds, style };
        return this.http
            .post<string[]>(
                RestApiBase.FULL_ASSET_PAGE_CITATION_ACTION,
                citationJson,
                {
                    params,
                },
            )
            .pipe(shareReplay());
    }

    updateStatistics(passedParams, action: string) {
        let fileId = passedParams.pid;

        if (action === 'VIEW' || action === 'DOWNLOAD') {
            action = 'SPECIFIC_FILE_REQUEST';
            if (!this._alreadyView) {
                this.updateStatistics(passedParams, 'FILE_REQUEST').subscribe();
            }
        } else if (action === 'FILE_REQUEST') {
            this._alreadyView = true;
            fileId = passedParams.mmsId;
        }

        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('repId', passedParams.repId)
            .set('mmsId', passedParams.mmsId)
            .set('pid', fileId)
            .set('type', action);
        return this.http
            .get(RestApiBase.UPDATE_STATISTICS, { params })
            .pipe(shareReplay());
    }

    updateMetadataViewStatistics(mmsId: string, repId) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('repId', repId)
            .set('mmsId', mmsId)
            .set('type', 'METADATA_VIEW');
        return this.http
            .get(RestApiBase.UPDATE_STATISTICS, { params })
            .pipe(shareReplay());
    }

    sendAssetByMail(mmsId, email) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('mmsID', mmsId)
            .set('language', this.i18nSvc.getLanguage())
            .set('email', email);
        return this.http
            .get(RestApiBase.SEND_ASSET_BY_MAIL, {
                params,
            })
            .pipe(shareReplay());
    }

    getTranslation(code, params = {}) {
        return this.translate.instant(code, params);
    }

    getCitationList() {
        const params = new HttpParams().set('institution', this.instCode);
        return this.http
            .get<string[]>(RestApiBase.GET_CITATION_LIST, {
                params,
            })
            .pipe(shareReplay());
    }

    // For arcview integration; see URM-170636
    getViewer(mmsId) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('mmsId', mmsId)
            .set('language', this.i18nSvc.getLanguage());
        return this.http.get(RestApiBase.GET_VIEWER_REPRESENTATION_INFO, {
            params,
        });
    }

    getInCitesHighlights(mmsId: string) {
        const params = new HttpParams().set('institution', this.instCode);
        return this.http
            .get<InciteMetricsDTO>(
                `${RestApiBase.API_ENDPOINT_INCITES}/highlights/${mmsId}`,
                { params },
            )
            .pipe(shareReplay());
    }

    getInCitesSdgs(mmsId: string) {
        const params = new HttpParams().set('institution', this.instCode);
        return this.http
            .get<
                SchemaValueDTO[]
            >(`${RestApiBase.API_ENDPOINT_INCITES}/sdgs/${mmsId}`, { params })
            .pipe(shareReplay());
    }

    getRevisionChanges(mmsId: string) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('mmsId', mmsId)
            .set('userId', this.jwtSvc.getUserID());

        return this.http
            .get<AssetRevisionChangesDTO>(
                `${RestApiBase.ASSET_REVISION}/changes`,
                { params },
            )
            .pipe(shareReplay());
    }
    getPendingChanges(mmsId: string) {
        const params = new HttpParams()
            .set('institution', this.instCode)
            .set('mmsId', mmsId)
            .set('userId', this.jwtSvc.getUserID());

        return this.http
            .get<AssetRevisionChangesDTO>(
                `${RestApiBase.ASSET_REVISION}/pendingChanges`,
                { params },
            )
            .pipe(shareReplay());
    }

    saveRevisionChanges(mmsId: string, changes: AssetRevisionChangeDTO[]) {
        const params = new HttpParams().set('institution', this.instCode);
        const changesObj: AssetRevisionChangesDTO = {
            mmsId,
            userId: this.jwtSvc.getUserID(),
            changes,
        };

        return this.http
            .post<void>(
                `${RestApiBase.ASSET_REVISION}/saveChanges`,
                changesObj,
                { params },
            )
            .pipe(shareReplay());
    }

    submitRevision(mmsId: string, note: string) {
        const params = new HttpParams().set('institution', this.instCode);
        const assetRevision: AssetRevisionSubmissionDTO = {
            mmsId,
            note,
            userId: this.jwtSvc.getUserID(),
        };

        return this.http
            .post<void>(`${RestApiBase.ASSET_REVISION}/submit`, assetRevision, {
                params,
            })
            .pipe(shareReplay());
    }

    getAssetMediaMentions(mmsID: string) {
        const params = new HttpParams().set('institution', this.instCode);
        return this.http
            .get(
                `${RestApiBase.GET_ASSET_FUILL_PAGE_BY_MMS_ID}/${mmsID}/mediaMentions`,
                { params },
            )
            .pipe(shareReplay());
    }

    updateSectionItem(sectionKey: any, item: any) {
        let mmsId = this.entityId;
        let assetFieldType = sectionKey;
        let value = item;

        switch (sectionKey) {
            case 'localFields':
                const lfObj = item as EntityLocalFields;
                mmsId = lfObj.parentId;
                assetFieldType = AssetFieldTypeEnum[lfObj.code];
                value = lfObj.localFieldValue;
                break;
        }
        const params = new HttpParams().set('institution', this.instCode);
        const changesObj: AssetRevisionChangesDTO = {
            mmsId,
            userId: this.jwtSvc.getUserID(),
            changes: [{ assetFieldType, value }],
        };

        return this.http
            .post(`${RestApiBase.ASSET_REVISION}/saveChanges`, changesObj, {
                params,
            })
            .pipe(
                shareReplay(),
                switchMap(() => of(item)),
            );
    }
}
